import React from "react";
import { BookingStatus } from "components/common/Card";
import { EllipsisOutlined, MessageOutlined } from "@ant-design/icons";
import { Icon } from "components/common";
import { colors } from "gstyles";
import { get, isFunction, map, isEmpty } from "utils/lodash";
import { BOOKING_STATUS } from "utils/constant";
import { formatDate, formatHour } from "utils/dateFormatter";

import CancelBooking from "./CancelBooking";
import AcceptBooking from "./AcceptBooking";
import DoneBooking from "./DoneBooking";
import NotComeBooking from "./NotComeBooking";
import ButtonGroup from "components/common/ButtonGroup";
import cx from "classnames";
import { useRouter } from "next/navigation";
import helper from "utils/helper";
import { message } from "antd";
import { useChatStore } from "stores/chatStore";

interface BookingCardProps {
  data: {
    time: string;
    customerName: string;
    service: string;
    status: keyof typeof BOOKING_STATUS;
    customerNumber?: number;
    date: string;
  };
  onMessage?: () => void;
  onMore?: (booking: any) => void;
  onAccept?: (booking: any) => void;
  onClick?: () => void;
}

const renderActionButton = (button: string) => {
  const ActionButtonComponent = ({
    booking,
    onCancelSuccess,
    onAcceptSuccess,
    onDoneSuccess,
    onNotComeSuccess,
  }: any) => {
    switch (button) {
      case "cancel":
        return <CancelBooking booking={booking} onSuccess={onCancelSuccess} />;
      case "accept":
        return <AcceptBooking booking={booking} onSuccess={onAcceptSuccess} />;
      case "done":
        return <DoneBooking booking={booking} onSuccess={onDoneSuccess} />;
      case "not_show":
        return (
          <NotComeBooking booking={booking} onSuccess={onNotComeSuccess} />
        );
      default:
        return null;
    }
  };
  ActionButtonComponent.displayName = `ActionButton_${button}`;
  return ActionButtonComponent;
};

const ActionButton = ({
  booking,
  onCancelSuccess,
  onAcceptSuccess,
  onDoneSuccess,
  onNotComeSuccess,
}: any) => {
  const status = get(booking, "status");
  const actionStatus = {
    waiting: ["accept", "cancel"],
    accepted: ["done", "not_show"],
  };
  const actions = get(actionStatus, status, []);
  return map(actions, (action) => {
    const Component = renderActionButton(action);
    return (
      <Component
        key={action}
        booking={booking}
        onCancelSuccess={onCancelSuccess}
        onAcceptSuccess={onAcceptSuccess}
        onDoneSuccess={onDoneSuccess}
        onNotComeSuccess={onNotComeSuccess}
      />
    );
  });
};

const BookingCard: React.FC<BookingCardProps> = ({
  data,
  onMessage,
  onMore,
  onAccept,
}) => {
  const [booking, $booking] = React.useState(data);
  const [isInitializingChat, setIsInitializingChat] = React.useState(false);
  const { initializeOrGetChatRoom } = useChatStore();

  const router = useRouter();
  const handler = {
    onCancelSuccess: (res?: any) => {
      const data = get(res, "data");
      if (!isEmpty(data)) {
        $booking(data);
      }
    },
    onAcceptSuccess: (res?: any) => {
      const data = get(res, "data");
      if (!isEmpty(data)) {
        $booking(data);
      }
      onAccept?.(data);
    },
    onDoneSuccess: (res?: any) => {
      const data = get(res, "data");
      if (!isEmpty(data)) {
        $booking(data);
      }
    },
    onNotComeSuccess: (res?: any) => {
      const data = get(res, "data");
      if (!isEmpty(data)) {
        $booking(data);
      }
    },
    onClick: () => {

      if (get(booking, "id")) {
        router.push(`/bookings/${get(booking, "id")}`);
      }
    },
    onChatClick: async (e: React.MouseEvent) => {
      e.stopPropagation();

      // Extract userId and shopId from booking data
      const userId = get(booking, "populatedUser[0].id") ||
                     get(booking, "populatedUser[0]._id") ||
                     get(booking, "user") ||
                     get(booking, "userId");

      const shopId = get(booking, "shop.id") ||
                     get(booking, "shop._id") ||
                     get(booking, "populatedShop[0].id") ||
                     get(booking, "populatedShop[0]._id") ||
                     get(booking, "shopId");

      if (!userId || !shopId) {
        message.error("Không thể khởi tạo chat: Thiếu thông tin cần thiết");
        return;
      }

      setIsInitializingChat(true);
      try {
        const chatRoom = await initializeOrGetChatRoom(userId, shopId);

        if (chatRoom?.id) {
          router.push(`/chat/${chatRoom.id}`);
        } else {
          message.error("Không thể khởi tạo phòng chat");
        }
      } catch (error) {
        console.error("Error initializing chat room:", error);
        message.error("Có lỗi xảy ra khi khởi tạo chat");
      } finally {
        setIsInitializingChat(false);
      }
    },
  };

  return (
    <div
      className="bg-white rounded-lg p-4 flex flex-col gap-1 shadow-[0_2px_8px_rgba(0,0,0,0.06)] cursor-pointer"
      onClick={handler.onClick}
    >
      <div className="flex justify-between items-center">
        <div className="text-lg font-medium">
          {get(booking, "populatedUser[0].name") ||
            (get(booking, "populatedUser[0].email") && helper.getEmailName(get(booking, "populatedUser[0].email"))) ||
            "Khách hàng"}
        </div>
        <div className="flex gap-3 text-gray-400">
          <div className="flex justify-start">
            <BookingStatus status={get(booking, "status") as any} />
          </div>
          <Icon
            name="more"
            color={colors.black06}
            size={22}
            className="cursor-pointer hover:text-gray-600"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              if (isFunction(onMore)) {
                onMore(booking);
              }
            }}
          />
          <Icon
            name="booking-message-inactive"
            color={isInitializingChat ? colors.gray : colors.black06}
            size={22}
            className={`cursor-pointer hover:text-gray-600 ${isInitializingChat ? 'opacity-50 cursor-wait' : ''}`}
            onClick={isInitializingChat ? undefined : handler.onChatClick}
          />
        </div>
      </div>

      <div className="text-sm flex gap-2 pt-2">
        <div>Đặt {get(booking, "customerNumber")} chỗ</div>
        <div>•</div>
        <div>{formatDate(get(booking, "date"))}</div>
        <div>•</div>
        <div>{formatHour(get(booking, "startTime", ""))}</div>
      </div>

      {get(booking, "code") && (
        <div className="text-sm text-gray-600 pt-1">
          Mã: <span className="font-medium text-gray-900">{get(booking, "code")}</span>
        </div>
      )}

      <div className="text-sm">{`Dịch vụ: ${map(
        get(booking, "serviceTransaction"),
        (item) => get(item, "service.name")
      ).join(", ")}`}</div>
      <div
        className="pt-2"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        <ButtonGroup spacing="sm">
          <ActionButton booking={booking} {...handler} />
        </ButtonGroup>
      </div>
    </div>
  );
};

export default BookingCard;
